/** GAZDA firului de cod: pornește-l, dă-i treaba, oprește-l.
 *
 *  DE CE ARE NEVOIE ASISTENTUL SĂ RULEZE COD. «Mută toți pereții interiori cu
 *  12 cm spre coridor, dar numai pe cei mai lungi de 2 m» s-ar putea scrie ca
 *  argumente. «Egalizează dormitoarele la aceeași lățime» nu: acolo trebuie o
 *  măsurătoare, o medie și o deplasare diferită pentru fiecare zid. Iar «fă X»
 *  pentru un X la care nu s-a gândit nimeni când s-au scris uneltele nu se
 *  poate scrie deloc — până când codul devine el unealta (v. `motor.ts`).
 *
 *  UNDE RULEAZĂ: în `nucleuCod.ts`, alt fir, cu porțile spre rețea tăiate. Aici
 *  rămâne doar ce ține de fir ca proces — ceasul, oprirea, mărimea răspunsului.
 *
 *  DOUĂ CEASURI, și al doilea e motivul handshake-ului. Firul aduce cu el
 *  API-ul editorului, adică un chunk de descărcat la primul apel din pagină;
 *  plafonul de CALCUL n-are de ce să plătească descărcarea aia, așa că se
 *  pornește abia când firul spune `gata`.
 *
 *  «A PICAT» ÎNAINTE DE `gata` E ALTĂ POVESTE, și trebuie spusă ca atare. Un
 *  fir care nu-și poate ADUCE fișierul dă un `onerror` fără mesaj, fără fișier
 *  și fără linie — exact ce dă și un cod care crapă, dacă te uiți doar la
 *  `ev.message`. Cazul care se întâmplă în realitate e o pagină rămasă
 *  deschisă peste o publicare: chunk-urile poartă hash în nume, cel vechi nu
 *  mai există pe server, iar `/assets/…` întoarce 404 fără să cadă pe
 *  `index.html`. Atunci PICĂ ORICE COD, și `1+1`, la fel, la nesfârșit, până
 *  la o reîncărcare — iar un «Firul de calcul a picat.» trimite omul să caute
 *  vina în codul lui. Proba costă un `fetch` pe chunk-ul paginii ăsteia
 *  (`import.meta.url`): dacă nu mai e pe server, pagina e veche și asta se
 *  scrie în clar.
 */
/** Cât poate socoti codul. Zece secunde, nu trei: firul e altul, deci ecranul
 *  rămâne al omului cât se lucrează, iar o măsurătoare peste toate camerele
 *  unui modul mare trece lejer de trei — și atunci pica nu codul, ci plafonul,
 *  sub un mesaj care arăta la fel. O buclă infinită costă acum zece secunde și
 *  un `terminate()`, ceea ce e un preț bun pentru a nu tăia lucrul adevărat. */
export const RASTIMP_MS = 10000;
export const RASTIMP_PORNIRE_MS = 15000;
const MAX_REZULTAT = 200_000;

export type RezultatCod =
  | { ok: true; valoare?: unknown; doc?: unknown }
  | { ok: false; eroare: string };

/** Chunk-ul din care rulează rândurile astea mai e pe server?
 *
 *  `no-store` fiindcă întrebarea e despre server, nu despre cache — copia din
 *  cache e chiar cea care face pagina să pară în regulă. Dacă proba însăși nu
 *  se poate face (rețea căzută), nu se declară nimic: `false` lasă mesajul
 *  general. */
async function paginaEVeche(): Promise<boolean> {
  try {
    const r = await fetch(import.meta.url, { method: "HEAD", cache: "no-store" });
    return !r.ok;
  } catch {
    return false;
  }
}

export const NEINCARCAT =
  "Firul de calcul nu s-a putut încărca (fișierul lui n-a venit de la "
  + "server), deci niciun cod nu poate rula acum. Spune-i omului să "
  + "reîncarce pagina și încearcă din nou.";
export const PAGINA_VECHE =
  "Firul de calcul nu s-a putut încărca: pagina e deschisă de dinaintea unei "
  + "publicări, iar fișierele ei nu mai există pe server. Niciun cod nu poate "
  + "rula până la o reîncărcare a paginii (⌘R / Ctrl+R) — spune-i omului "
  + "asta, nu mai încerca alt cod.";

/** Codul, rulat în fir — cu A DOUA ÎNCERCARE dacă firul n-a putut porni.
 *
 *  Fișierul firului se cere prin rețea la primul cod din pagină, iar o rețea
 *  care clipește o dată nu e un motiv să-i spui omului că nu se poate. Se reia
 *  o singură dată, și NUMAI când firul n-a apucat să pornească: o cădere din
 *  cod se întoarce ca atare, iar pe o pagină veche (v. antetul) a doua
 *  încercare ar cere exact fișierul care nu mai există. */
export async function ruleazaCod(cod: string, doc: unknown, plan: unknown,
                                 opt?: { vreaDoc?: boolean; calc?: unknown },
): Promise<RezultatCod> {
  const r = await oIncercare(cod, doc, plan, opt);
  if (r.ok || r.eroare !== NEINCARCAT) return r;
  await new Promise((iar) => setTimeout(iar, 250));
  return oIncercare(cod, doc, plan, opt);
}

function oIncercare(cod: string, doc: unknown, plan: unknown,
                    opt?: { vreaDoc?: boolean; calc?: unknown },
): Promise<RezultatCod> {
  return new Promise((gata) => {
    let lucrator: Worker | null = null;
    let ceas: ReturnType<typeof setTimeout> | null = null;
    let pornit = false;
    const inchide = () => {
      if (ceas) clearTimeout(ceas);
      lucrator?.terminate();
    };
    const raspunde = (r: RezultatCod) => { inchide(); gata(r); };

    try {
      lucrator = new Worker(new URL("./nucleuCod.ts", import.meta.url),
                            { type: "module" });
    } catch (err) {
      raspunde({ ok: false, eroare: `Nu am putut porni firul de calcul: ${err}` });
      return;
    }
    ceas = setTimeout(() => raspunde({
      ok: false,
      eroare: `Firul de calcul nu a pornit în ${RASTIMP_PORNIRE_MS} ms.`,
    }), RASTIMP_PORNIRE_MS);

    lucrator.onmessage = (ev: MessageEvent) => {
      if (ev.data?.gata) {
        pornit = true;
        if (ceas) clearTimeout(ceas);
        ceas = setTimeout(() => raspunde({
          ok: false,
          eroare: `Codul a depășit ${RASTIMP_MS} ms și a fost oprit.`,
        }), RASTIMP_MS);
        return;
      }
      const r = ev.data as RezultatCod;
      if (!r.ok) { raspunde(r); return; }
      // CE SE ÎNTOARCE TRECE PRIN JSON: e ce va citi modelul (sau, la editare,
      // ce intră în document), iar un obiect cu cicluri sau cu funcții n-ar
      // avea cum să ajungă acolo.
      try {
        const text = JSON.stringify(("doc" in r ? r.doc : r.valoare) ?? null);
        if (text.length > MAX_REZULTAT) {
          raspunde({ ok: false, eroare:
            `Rezultatul are ${text.length} caractere; întoarce mai puțin `
            + "(numără, filtrează, taie lista)." });
          return;
        }
        raspunde("doc" in r ? { ok: true, doc: JSON.parse(text) }
                            : { ok: true, valoare: JSON.parse(text) });
      } catch {
        raspunde({ ok: false, eroare: "Rezultatul nu se poate serializa (cicluri?)." });
      }
    };
    lucrator.onerror = (ev) => {
      if (ev.message || pornit) {
        raspunde({ ok: false, eroare: ev.message || "Firul de calcul a picat." });
        return;
      }
      // Fără mesaj ȘI fără `gata`: fișierul firului n-a ajuns până aici. Firul
      // se oprește acum, dar răspunsul așteaptă proba paginii.
      inchide();
      paginaEVeche().then((veche) => gata({
        ok: false, eroare: veche ? PAGINA_VECHE : NEINCARCAT,
      }));
    };
    // Copia: mesajul e clonat la trimitere, deci firul primește propriile lui
    // obiecte. Se trimite acum, nu la `gata` — mesajele plecate înainte de
    // încărcarea modulului stau la coadă.
    lucrator.postMessage({ cod, doc, plan, calc: opt?.calc ?? null,
                           vreaDoc: !!opt?.vreaDoc });
  });
}
